import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { KizenTypography } from '__app/typography';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { mcpServerHistoryQuery } from 'query/mcp';
import { useColumns } from 'components/Modals/HistoryModal/hooks';
import { HistoryDetailsModal } from 'components/Modals/HistoryModal/HistoryDetailsModal';
import {
  StyledPaginationWrapper,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from 'pages/MCPServerSettings/styles';
import { DEFAULT_SERVER_HISTORY_PAGINATION_SETTINGS } from 'pages/MCPServerSettings/constants';
import type { MCPServerHistoryEntry } from '@kizen/api/mcp';

export const MCPServerTimeline = () => {
  const { t } = useTranslation();
  const { mcpServerId } = useParams<{ mcpServerId: string }>();

  const { page, pageSize, ordering, handleChangePage, handleChangePageSize, headData } =
    usePaginationSettings(DEFAULT_SERVER_HISTORY_PAGINATION_SETTINGS);

  const [timelineEvent, setTimelineEvent] =
    useState<MCPServerHistoryEntry | null>(null);

  const queryOptions = useMemo(
    () =>
      mcpServerHistoryQuery(mcpServerId, {
        params: { page, page_size: pageSize, ordering },
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!mcpServerId,
      }),
    [mcpServerId, page, pageSize, ordering]
  );

  const { data, isLoading } = useQuery(queryOptions);

  const handleSelectAction = useCallback(
    ({ value }: { value: 'view' }, data: MCPServerHistoryEntry) => {
      if (value === 'view') {
        setTimelineEvent(data);
      }
    },
    []
  );

  const handleHide = useCallback(() => {
    setTimelineEvent(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  return (
    <>
      <StyledTableToolbar>
        <div />
        <KizenTypography as="h2" type="header">
          {t('MCP Server History Log')}
        </KizenTypography>
        <div />
      </StyledTableToolbar>
      <StyledTableWrapper>
        <StyledTableScrollContainer bottom top gradient={<Gradient />}>
          <BasicTable
            stickyHeader
            head={<TRow head columns={columns} data={headData} />}
            data-qa="mcp-server-history-table"
            staleData={isLoading}
          >
            {data?.count ? null : <TRow />}
            {(data?.results ?? []).map((entry) => (
              <TRow key={entry.id} columns={columns} data={entry} />
            ))}
          </BasicTable>
        </StyledTableScrollContainer>
      </StyledTableWrapper>
      <StyledPaginationWrapper>
        <TablePagination
          page={page}
          perPage={pageSize}
          totalCount={data?.count || 0}
          onChangePage={handleChangePage}
          onChangePerPage={handleChangePageSize}
        />
      </StyledPaginationWrapper>
      {timelineEvent ? (
        <HistoryDetailsModal data={timelineEvent.data} onHide={handleHide} />
      ) : null}
    </>
  );
};
